import { useEffect, useState } from "react";
import { Check, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { ApiError } from "@/lib/api";
import {
  createHermesApi,
  type HermesScoreOptimizePlanItem,
} from "@/lib/hermes-api";
import { brandSiteScoreOptimizePlanPageLabel } from "@/lib/hermes-brand-site-score-optimize-plan-ui";

type Decision = "loading" | "pending" | "accepted" | "rejected";

export function BrandSiteScoreOptimizeConfirmCard({
  accountId,
  chatId,
  planId,
  plan,
}: {
  accountId: string;
  chatId: number;
  planId: string;
  plan: HermesScoreOptimizePlanItem[];
}) {
  const [decision, setDecision] = useState<Decision>("loading");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void createHermesApi(accountId)
      .getScoreOptimizePlan(chatId)
      .then((state) => {
        if (cancelled) return;
        if (state.plan_id !== planId) {
          setDecision("rejected");
          return;
        }
        setDecision(
          state.status === "executing"
            ? "accepted"
            : state.status === "planning"
              ? "rejected"
              : "pending",
        );
      })
      .catch(() => {
        if (!cancelled) setDecision("pending");
      });
    return () => {
      cancelled = true;
    };
  }, [accountId, chatId, planId]);

  async function decide(action: "confirm" | "reject") {
    if (decision !== "pending" || busy) return;
    setBusy(true);
    try {
      const hermes = createHermesApi(accountId);
      const state =
        action === "confirm"
          ? await hermes.confirmScoreOptimizePlan(chatId, planId)
          : await hermes.rejectScoreOptimizePlan(chatId, planId);
      setDecision(state.status === "executing" ? "accepted" : "rejected");
      toast.success(action === "confirm" ? "好的，开始按计划改草稿" : "已退回，请 Hermes 重新说清楚");
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : "操作失败，请稍后重试");
    } finally {
      setBusy(false);
    }
  }

  const decided = decision === "accepted" || decision === "rejected";

  return (
    <div className="w-full rounded-xl border border-amber-300/70 bg-amber-50/70 p-3 text-sm shadow-xs dark:border-amber-800/70 dark:bg-amber-950/20">
      <p className="font-medium text-foreground">请确认：准备改这些地方</p>
      <p className="mt-0.5 text-xs text-muted-foreground">
        {decided
          ? decision === "accepted"
            ? "已确认。Hermes 只会改下面列出的页面草稿，不会自动发布上线。"
            : "已退回。请让 Hermes 用更清楚的中文重新写计划。"
          : "点「可以，按这个改」后才会动草稿；不会马上发布到访客看到的网站。"}
      </p>

      <ol className="mt-3 space-y-2">
        {plan.map((item, index) => (
          <li key={`${item.slug}-${index}`} className="rounded-lg border border-border/60 bg-background/80 p-2.5">
            <div className="flex items-start gap-2">
              <span className="flex size-5 shrink-0 items-center justify-center rounded-full bg-muted text-[11px] font-medium">
                {index + 1}
              </span>
              <div className="min-w-0 space-y-0.5">
                <p className="font-medium text-foreground">
                  {brandSiteScoreOptimizePlanPageLabel(item.slug)}
                </p>
                <p className="text-sm leading-snug text-foreground/90">{item.action}</p>
                {item.expect ? (
                  <p className="text-[11px] text-muted-foreground">改完后：{item.expect}</p>
                ) : null}
              </div>
            </div>
          </li>
        ))}
      </ol>

      <div className="mt-3 flex flex-wrap gap-2">
        <Button
          size="sm"
          disabled={decision !== "pending" || busy}
          onClick={() => void decide("confirm")}
        >
          <Check className="size-4" />
          可以，按这个改
        </Button>
        <Button
          variant="outline"
          size="sm"
          disabled={decision !== "pending" || busy}
          onClick={() => void decide("reject")}
        >
          <X className="size-4" />
          不对，重说
        </Button>
      </div>
    </div>
  );
}
